import React from 'react';
import { Box, Flex, Heading, Link, Text } from 'pounceV2';
import { useTheme } from '@mui/material';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import GenericCodeBlockV3 from 'Components/CodeBlock/GenericCodeBlockV3';
import { stripSkillMentions } from 'Components/AI/Conversations/constants';

export const PROMPT_MARKDOWN_REMARK_PLUGINS = [remarkGfm];

export const usePromptMarkdownComponents = () => {
  const theme = useTheme();

  return React.useMemo(
    () => ({
      p: ({ children }: { children?: React.ReactNode }) => (
        <Text my={1} wordBreak="break-word" whiteSpace="pre-wrap">
          {children}
        </Text>
      ),
      h1: ({ children }: { children?: React.ReactNode }) => (
        <Heading variant="h4" fontWeight="semibold" wordBreak="break-word">
          {children}
        </Heading>
      ),
      h2: ({ children }: { children?: React.ReactNode }) => (
        <Heading variant="h5" fontWeight="semibold" wordBreak="break-word">
          {children}
        </Heading>
      ),
      h3: ({ children }: { children?: React.ReactNode }) => (
        <Heading variant="h6" fontWeight="semibold" wordBreak="break-word">
          {children}
        </Heading>
      ),
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
        <Link href={href} external target="_blank" wordBreak="break-word">
          {children}
        </Link>
      ),
      ul: ({ children }: { children?: React.ReactNode }) => (
        <Flex as="ul" ml={6} direction="column" gap={1} sx={{ listStyle: 'disc' }}>
          {children}
        </Flex>
      ),
      ol: ({ children }: { children?: React.ReactNode }) => (
        <Flex as="ol" ml={6} direction="column" gap={1} sx={{ listStyle: 'decimal' }}>
          {children}
        </Flex>
      ),
      li: ({ children }: { children?: React.ReactNode }) => (
        <Box as="li" wordBreak="break-word">
          {children}
        </Box>
      ),
      // Split pre/code so inline `code` stays inline (matches EnrichedAIResponseV3).
      pre: ({
        children,
      }: {
        children?: React.ReactElement<{ className?: string; children?: React.ReactNode }>;
      }) => {
        const language = /language-(\w+)/.exec(children?.props?.className || '')?.[1];
        if (language) {
          return (
            <GenericCodeBlockV3 language={language}>{children?.props?.children}</GenericCodeBlockV3>
          );
        }
        return (
          <Box
            as="pre"
            p={1}
            backgroundColor={theme.palette.tokens.background.brand.dark[2]}
            borderRadius="4px"
            overflow="auto"
            my={1}
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}
          >
            <Text fontFamily="mono" fontSize={13}>
              {children?.props?.children}
            </Text>
          </Box>
        );
      },
      code: ({ children }: { children?: React.ReactNode }) => (
        <Box
          as="code"
          px={0.5}
          backgroundColor={theme.palette.tokens.background.brand.dark[2]}
          borderRadius="4px"
          fontFamily="mono"
          fontSize={13}
        >
          {children}
        </Box>
      ),
    }),
    [theme],
  );
};

interface PromptMarkdownPreviewV3Props {
  value: string;
}

/**
 * Read-only renderer for prompt markdown. Used by chat bubbles, scheduled
 * prompt details, and `FormikMarkdownTextAreaV3`'s preview mode so all surfaces share
 * the same component mappings and the same skill-mention strip.
 */
export const PromptMarkdownPreviewV3: React.FC<PromptMarkdownPreviewV3Props> = ({ value }) => {
  const components = usePromptMarkdownComponents();
  const rendered = React.useMemo(() => stripSkillMentions(value), [value]);
  return (
    <Box
      sx={{
        // Collapse softbreaks to spaces (CommonMark default) regardless of any inherited
        // `white-space: pre-wrap` from a parent (e.g. CollapsibleTextV3).
        whiteSpace: 'normal',
        // Explicit length-valued line-height so CollapsibleTextV3's clamp threshold can
        // read a deterministic value from this element (its `firstElementChild` lookup).
        lineHeight: 1.5,
        // :first-child / :last-child (not :first-of-type): markdown emits mixed siblings.
        '& > :first-child': { mt: 0 },
        '& > :last-child': { mb: 0 },
      }}
    >
      <Markdown remarkPlugins={PROMPT_MARKDOWN_REMARK_PLUGINS} components={components}>
        {rendered}
      </Markdown>
    </Box>
  );
};
